import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useUser } from '@/contexts/UserContext';

export interface DealTask {
  id: string;
  deal_id: string;
  title: string;
  description?: string;
  status?: string;
  assigned_to?: string;
  due_date?: string;
  start_date?: string;
  completion_date?: string;
  progress_percentage?: number;
  is_milestone?: boolean;
  payment_amount?: number;
  payment_status?: string;
}

export interface DealDetail {
  id: string;
  title: string;
  description?: string;
  deal_value?: number;
  deal_type?: string;
  status?: string;
  priority?: string;
  estimated_completion_date?: string;
  actual_completion_date?: string;
  brand_profile_id?: string;
  talent_profile_id?: string;
  venture_profile_id?: string;
  agent_profile_id?: string;
  campaign_id?: string;
  created_at: string;
  updated_at: string;
  deal_tasks?: DealTask[];
  talent?: {
    id: string;
    name: string;
    sport?: string;
    photo_url?: string;
  };
  brand?: {
    company_name?: string;
    logo_url?: string;
  };
}

export const useDealDetail = (dealId: string | undefined) => {
  const { currentUser } = useUser();
  const [deal, setDeal] = useState<DealDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchDeal = useCallback(async () => {
    if (!dealId || !currentUser?.id) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Fetch deal with tasks
      const { data: dealData, error: dealError } = await supabase
        .from('deals')
        .select(`
          *,
          deal_tasks (*)
        `)
        .eq('id', dealId)
        .single();

      if (dealError) throw dealError;

      // Fetch talent info if talent_profile_id exists
      let talentInfo = null;
      if (dealData.talent_profile_id) {
        const { data: talent } = await supabase
          .from('talents')
          .select('id, name, sport, photo_url')
          .eq('profile_id', dealData.talent_profile_id)
          .single();
        talentInfo = talent;
      }

      // Fetch brand info if brand_profile_id exists
      let brandInfo = null;
      if (dealData.brand_profile_id) {
        const { data: brand } = await supabase
          .from('brands')
          .select('company_name, logo_url')
          .eq('profile_id', dealData.brand_profile_id)
          .single();
        brandInfo = brand;
      }

      setDeal({
        ...dealData,
        talent: talentInfo,
        brand: brandInfo,
      });
    } catch (err: any) {
      console.error('Error fetching deal:', err);
      setError(err.message || 'Failed to fetch deal');
    } finally {
      setLoading(false);
    }
  }, [dealId, currentUser?.id]);

  useEffect(() => {
    fetchDeal();
  }, [fetchDeal]);

  const updateTask = async (taskId: string, updates: Partial<DealTask>) => {
    try {
      const { error } = await supabase
        .from('deal_tasks')
        .update(updates)
        .eq('id', taskId);

      if (error) throw error;
      await fetchDeal();
    } catch (err: any) {
      console.error('Error updating task:', err);
      throw err;
    }
  };

  const createTask = async (taskData: Omit<DealTask, 'id' | 'deal_id'>) => {
    if (!dealId) return;

    try {
      const { error } = await supabase
        .from('deal_tasks')
        .insert({
          ...taskData,
          deal_id: dealId,
        });

      if (error) throw error;
      await fetchDeal();
    } catch (err: any) {
      console.error('Error creating task:', err);
      throw err;
    }
  };

  const deleteTask = async (taskId: string) => {
    try {
      const { error } = await supabase
        .from('deal_tasks')
        .delete()
        .eq('id', taskId);

      if (error) throw error;
      await fetchDeal();
    } catch (err: any) {
      console.error('Error deleting task:', err);
      throw err;
    }
  };

  return {
    deal,
    loading,
    error,
    fetchDeal,
    updateTask,
    createTask,
    deleteTask,
  };
};
